import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import SEOHelmet from '../components/SEOHelmet';

const CASE_STUDIES = [
  {
    id: 'commerce-marketplace',
    company: 'Multi-Vendor Marketplace',
    industry: 'E-commerce',
    challenge: 'Vendors, inventory, checkout, payouts, and admin operations were spread across manual workflows.',
    solution: 'Built a marketplace platform with vendor dashboards, product controls, cart, checkout, payment flows, and real-time inventory updates.',
    result: '3x faster order processing',
    resultNumber: '3x',
    resultMetric: 'Faster Processing',
    timeline: '10 weeks',
    image: 'https://res.cloudinary.com/dwrlbuej9/image/upload/v1775034833/demo-ecommerce_h9hkpn.png',
    services: ['E-commerce', 'Custom Web App', 'Payment Integration'],
    stack: ['React', 'Node.js', 'MongoDB', 'Stripe'],
    highlights: ['Vendor dashboards', 'Inventory sync', 'Checkout optimization'],
    color: '#2563eb',
  },
  {
    id: 'travel-platform',
    company: 'Travel Booking Platform',
    industry: 'Travel',
    challenge: 'The team needed a polished booking presence with package browsing, customer requests, and admin visibility.',
    solution: 'Delivered a travel website with tour package listings, booking management, customer portal, and analytics-ready admin workflow.',
    result: 'booking operations centralized',
    resultNumber: '1',
    resultMetric: 'Unified Workflow',
    timeline: '8 weeks',
    image: 'https://res.cloudinary.com/dwrlbuej9/image/upload/v1775647274/Screenshot_2026-04-08_172050_kp9p2l.png',
    services: ['Web Development', 'Booking System', 'Dashboard'],
    stack: ['React', 'Chart.js', 'Node.js', 'MongoDB'],
    highlights: ['Package management', 'Booking dashboard', 'Customer portal'],
    color: '#059669',
  },
  {
    id: 'security-monitoring',
    company: 'Site Monitor SaaS',
    industry: 'SaaS / Security',
    challenge: 'Website owners needed a simple way to monitor uptime, backups, subscriptions, and site health.',
    solution: 'Created a monitoring SaaS with uptime checks, backup workflows, restore actions, dashboard insights, and Stripe subscriptions.',
    result: '24/7 monitoring capability',
    resultNumber: '24/7',
    resultMetric: 'Monitoring',
    timeline: '9 weeks',
    image: 'https://res.cloudinary.com/dwrlbuej9/image/upload/v1776311265/Screenshot_2026-04-16_094424_wtr6is.png',
    services: ['SaaS Development', 'Cloud Infrastructure', 'Stripe Billing'],
    stack: ['React', 'Node.js', 'MongoDB', 'Stripe'],
    highlights: ['Uptime dashboard', 'Automated backups', 'Subscription billing'],
    color: '#dc2626',
  },
  {
    id: 'employee-ops',
    company: 'Employee Management System',
    industry: 'Operations',
    challenge: 'HR, attendance, leave approvals, performance records, and location updates were difficult to track in one place.',
    solution: 'Designed an operations system with employee records, attendance, leave flows, admin views, and Google Maps location tracking.',
    result: 'role-based operations shipped',
    resultNumber: '3',
    resultMetric: 'User Portals',
    timeline: '7 weeks',
    image: 'https://res.cloudinary.com/dwrlbuej9/image/upload/v1775050560/Screenshot_2026-04-01_193524_ym6qrg.png',
    services: ['Custom System', 'Automation', 'Admin Portal'],
    stack: ['React', 'Node.js', 'MongoDB', 'Google Maps API'],
    highlights: ['Attendance workflow', 'Leave approvals', 'Location tracking'],
    color: '#d97706',
  },
  {
    id: 'restaurant-pos',
    company: 'Restaurant POS System',
    industry: 'Food & Delivery',
    challenge: 'Restaurant teams needed faster order handling, table-wise management, payment support, and inventory visibility.',
    solution: 'Built a POS platform with table orders, inventory alerts, payment flows, staff dashboards, and daily sales reporting.',
    result: 'inventory alerts automated',
    resultNumber: '100%',
    resultMetric: 'Order Visibility',
    timeline: '6 weeks',
    image: 'https://res.cloudinary.com/dwrlbuej9/image/upload/v1775052627/download_yyfjyh.png',
    services: ['POS System', 'Analytics', 'Payment Integration'],
    stack: ['React', 'Node.js', 'MongoDB', 'JWT'],
    highlights: ['Table-wise orders', 'Low-stock alerts', 'Sales reporting'],
    color: '#0891b2',
  },
  {
    id: 'crm-automation',
    company: 'CRM Workflow Automation',
    industry: 'Business Automation',
    challenge: 'Manual data entry, scattered customer records, and repetitive handoffs slowed the sales and operations team.',
    solution: 'Connected CRM records, lead flows, notifications, and repeatable business actions through automation-first workflows.',
    result: '80% less manual work',
    resultNumber: '80%',
    resultMetric: 'Manual Work Reduced',
    timeline: '4 weeks',
    image: 'https://res.cloudinary.com/dwrlbuej9/image/upload/v1775049937/demo-video-agency_t5w0yu.png',
    services: ['Automation', 'CRM Integration', 'Workflow Design'],
    stack: ['React', 'Node.js', 'Zapier', 'JWT'],
    highlights: ['Lead routing', 'CRM integration', 'Automated follow-ups'],
    color: '#7c3aed',
  },
];

const TESTIMONIALS = [
  {
    quote: 'The team understood our operation quickly and converted a messy workflow into a product our staff can actually use every day.',
    author: 'Operations Lead',
    company: 'Service Business',
    role: 'Director',
  },
  {
    quote: 'Clear communication, clean execution, and practical suggestions. They delivered the platform without turning the project into a black box.',
    author: 'Startup Founder',
    company: 'SaaS Product',
    role: 'Founder',
  },
  {
    quote: 'Our biggest concern was reliability. AxentraLab handled the infrastructure, launch, and post-launch support with real ownership.',
    author: 'Business Owner',
    company: 'E-commerce Brand',
    role: 'Owner',
  },
];

const IMPACT = [
  ['3x', 'Faster order processing'],
  ['80%', 'Less manual admin work'],
  ['24/7', 'Monitoring-ready systems'],
  ['10+', 'Stacks delivered'],
];

const METHOD = [
  { step: '01', title: 'Problem framing', desc: 'We define the operational bottleneck, target users, business metric, and delivery constraints.' },
  { step: '02', title: 'Workflow design', desc: 'We map screens, roles, integrations, and data movement before development accelerates.' },
  { step: '03', title: 'Focused build', desc: 'We ship the highest-value features first, then refine with review cycles and quality checks.' },
  { step: '04', title: 'Launch support', desc: 'We handle deployment, handover, training, and post-launch iteration so the system keeps working.' },
];

export default function CaseStudiesPage() {
  const [industry, setIndustry] = useState('All');
  const [activeCase, setActiveCase] = useState(CASE_STUDIES[0].id);

  const industries = ['All', ...Array.from(new Set(CASE_STUDIES.map((study) => study.industry)))];

  const filteredStudies = useMemo(() => {
    if (industry === 'All') return CASE_STUDIES;
    return CASE_STUDIES.filter((study) => study.industry === industry);
  }, [industry]);

  const selectedStudy = CASE_STUDIES.find((study) => study.id === activeCase) || filteredStudies[0] || CASE_STUDIES[0];

  return (
    <>
      <SEOHelmet
        title="Case Studies & Success Stories | AxentraLab"
        description="Explore AxentraLab case studies across e-commerce, SaaS, automation, travel, POS, and custom business systems."
        keywords="case studies, software development results, SaaS case study, e-commerce case study, automation success stories"
      />

      <div style={{ background: 'var(--bg)', minHeight: '100vh', overflowX: 'hidden' }}>
        <style>{`
          @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: none; } }

          .case-shell { max-width: 1180px; margin: 0 auto; padding: 0 5%; }
          .case-label {
            display: inline-flex;
            align-items: center;
            gap: 10px;
            color: var(--teal);
            font-family: var(--font-m);
            font-size: 12px;
            font-weight: 800;
            letter-spacing: 1.5px;
            text-transform: uppercase;
            margin-bottom: 18px;
          }
          .case-label::before {
            content: '';
            width: 28px;
            height: 2px;
            border-radius: 99px;
            background: var(--teal);
          }
          .filter-chip {
            min-height: 38px;
            padding: 9px 14px;
            border-radius: 8px;
            border: 1px solid var(--border);
            background: #fff;
            color: var(--muted);
            font-size: 13px;
            font-weight: 700;
            transition: all 0.2s ease;
          }
          .filter-chip.active {
            background: var(--teal);
            color: #fff;
            border-color: var(--teal);
            box-shadow: 0 10px 22px rgba(37,99,235,0.16);
          }
          .impact-grid,
          .method-grid,
          .testimonial-grid {
            display: grid;
            grid-template-columns: repeat(4, minmax(0, 1fr));
            gap: 16px;
          }
          .case-feature {
            display: grid;
            grid-template-columns: 0.95fr 1.05fr;
            border: 1px solid var(--border);
            border-radius: 8px;
            overflow: hidden;
            background: #fff;
            box-shadow: 0 24px 60px -36px rgba(15,23,42,0.45);
          }
          .study-list {
            display: grid;
            gap: 12px;
          }
          .study-tab {
            width: 100%;
            text-align: left;
            border: 1px solid var(--border);
            border-radius: 8px;
            background: #fff;
            padding: 18px;
            transition: all 0.2s ease;
          }
          .study-tab.active {
            border-color: rgba(37,99,235,0.35);
            box-shadow: 0 16px 38px -30px rgba(15,23,42,0.42);
          }
          .detail-card,
          .method-card,
          .testimonial-card {
            border: 1px solid var(--border);
            border-radius: 8px;
            background: #fff;
            padding: 24px;
          }
          .service-pill {
            display: inline-flex;
            align-items: center;
            min-height: 28px;
            padding: 6px 10px;
            border-radius: 8px;
            border: 1px solid var(--border);
            background: #fff;
            color: var(--muted);
            font-size: 11px;
            font-family: var(--font-m);
            font-weight: 700;
          }

          @media (max-width: 980px) {
            .case-feature { grid-template-columns: 1fr; }
            .impact-grid,
            .method-grid,
            .testimonial-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          }
          @media (max-width: 680px) {
            .impact-grid,
            .method-grid,
            .testimonial-grid { grid-template-columns: 1fr; }
            .hero-title { font-size: 42px !important; }
          }
          @media (prefers-reduced-motion: reduce) {
            *, *::before, *::after { animation: none !important; transition: none !important; }
          }
        `}</style>

        <section style={{ padding: '150px 0 76px', position: 'relative', overflow: 'hidden', background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)' }}>
          <div style={{
            position: 'absolute',
            inset: 0,
            backgroundImage: 'linear-gradient(var(--border) 1px,transparent 1px),linear-gradient(90deg,var(--border) 1px,transparent 1px)',
            backgroundSize: '64px 64px',
            opacity: 0.44,
          }} />
          <div className="case-shell" style={{ position: 'relative', zIndex: 1 }}>
            <div style={{ maxWidth: 880, animation: 'fadeUp 0.7s ease both' }}>
              <div className="case-label">Case Studies</div>
              <h1 className="hero-title" style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(48px, 7vw, 86px)', lineHeight: 0.98, color: 'var(--text)', margin: '0 0 24px', fontWeight: 900 }}>
                Proof that good software changes the way teams work.
              </h1>
              <p style={{ color: 'var(--muted)', fontSize: 18, lineHeight: 1.75, maxWidth: 680, marginBottom: 34 }}>
                Realistic project stories from business systems, e-commerce, travel platforms, SaaS products, POS systems, and automation workflows.
              </p>
              <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
                <a href="#case-study-work" className="btn-primary" style={{ padding: '15px 30px' }}>
                  View Outcomes -&gt;
                </a>
                <Link to="/contact" className="btn-outline" style={{ padding: '15px 28px' }}>
                  Discuss Your Case
                </Link>
              </div>
            </div>

            <div className="impact-grid" style={{ marginTop: 54 }}>
              {IMPACT.map(([value, label]) => (
                <div key={label} style={{ border: '1px solid var(--border)', borderRadius: 8, background: 'rgba(255,255,255,0.82)', padding: '22px 20px' }}>
                  <div style={{ fontFamily: 'var(--font-h)', fontSize: 34, fontWeight: 900, color: 'var(--text)', lineHeight: 1 }}>{value}</div>
                  <div style={{ marginTop: 8, color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 12 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="case-study-work" className="case-shell" style={{ padding: '76px 5% 96px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 24, flexWrap: 'wrap', marginBottom: 30 }}>
            <div>
              <div className="case-label">Selected Outcomes</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 52px)', lineHeight: 1.05, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                Browse the problems we solved and the systems we shipped.
              </h2>
            </div>
            <div style={{ color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 13 }}>
              {filteredStudies.length} case studies
            </div>
          </div>

          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 28 }}>
            {industries.map((item) => (
              <button
                key={item}
                type="button"
                className={`filter-chip ${industry === item ? 'active' : ''}`}
                onClick={() => {
                  setIndustry(item);
                  const next = item === 'All' ? CASE_STUDIES[0] : CASE_STUDIES.find((study) => study.industry === item);
                  if (next) setActiveCase(next.id);
                }}
              >
                {item}
              </button>
            ))}
          </div>

          <div className="case-feature">
            <div style={{ padding: 'clamp(22px, 4vw, 34px)', background: '#f8fafc', borderRight: '1px solid var(--border)' }}>
              <div className="study-list">
                {filteredStudies.map((study) => (
                  <button
                    key={study.id}
                    type="button"
                    className={`study-tab ${selectedStudy.id === study.id ? 'active' : ''}`}
                    onClick={() => setActiveCase(study.id)}
                  >
                    <div style={{ color: study.color, fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 8 }}>
                      {study.industry}
                    </div>
                    <div style={{ fontFamily: 'var(--font-h)', color: 'var(--text)', fontSize: 20, fontWeight: 900, marginBottom: 8 }}>
                      {study.company}
                    </div>
                    <div style={{ color: 'var(--muted)', fontSize: 13, lineHeight: 1.55 }}>
                      {study.result}
                    </div>
                  </button>
                ))}
              </div>
            </div>

            <div>
              <div style={{ aspectRatio: '16 / 8.4', background: '#e2e8f0', overflow: 'hidden' }}>
                <img src={selectedStudy.image} alt={selectedStudy.company} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
              </div>
              <div style={{ padding: 'clamp(26px, 5vw, 44px)' }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap', marginBottom: 18 }}>
                  <span style={{ color: selectedStudy.color, fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase' }}>{selectedStudy.industry}</span>
                  <span style={{ color: 'var(--muted)', fontSize: 12, fontFamily: 'var(--font-m)' }}>{selectedStudy.timeline}</span>
                </div>
                <h3 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 5vw, 54px)', lineHeight: 1, color: 'var(--text)', margin: '0 0 18px', fontWeight: 900 }}>
                  {selectedStudy.company}
                </h3>
                <div style={{ display: 'grid', gridTemplateColumns: 'minmax(120px, 180px) 1fr', gap: 18, alignItems: 'center', marginBottom: 28 }}>
                  <div style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 18, textAlign: 'center' }}>
                    <div style={{ fontFamily: 'var(--font-h)', fontSize: 42, fontWeight: 900, color: selectedStudy.color, lineHeight: 1 }}>{selectedStudy.resultNumber}</div>
                    <div style={{ marginTop: 8, color: 'var(--muted)', fontFamily: 'var(--font-m)', fontSize: 11 }}>{selectedStudy.resultMetric}</div>
                  </div>
                  <p style={{ color: 'var(--muted)', fontSize: 15, lineHeight: 1.75 }}>
                    {selectedStudy.solution}
                  </p>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 14 }} className="testimonial-grid">
                  <div className="detail-card">
                    <div className="case-label" style={{ marginBottom: 12 }}>Challenge</div>
                    <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>{selectedStudy.challenge}</p>
                  </div>
                  <div className="detail-card">
                    <div className="case-label" style={{ marginBottom: 12 }}>Highlights</div>
                    <div style={{ display: 'grid', gap: 10 }}>
                      {selectedStudy.highlights.map((item) => (
                        <div key={item} style={{ display: 'flex', gap: 10, color: 'var(--text)', fontSize: 14 }}>
                          <span style={{ color: selectedStudy.color, fontWeight: 900 }}>-</span>
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 20 }}>
                  {[...selectedStudy.services, ...selectedStudy.stack].map((item) => (
                    <span key={item} className="service-pill">{item}</span>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section style={{ padding: '82px 0', background: '#fff', borderTop: '1px solid var(--border)', borderBottom: '1px solid var(--border)' }}>
          <div className="case-shell">
            <div style={{ textAlign: 'center', marginBottom: 42 }}>
              <div className="case-label" style={{ justifyContent: 'center' }}>Method</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 48px)', lineHeight: 1.08, color: 'var(--text)', margin: 0, fontWeight: 900 }}>
                We turn business friction into clear product scope.
              </h2>
            </div>
            <div className="method-grid">
              {METHOD.map((item) => (
                <div key={item.step} className="method-card">
                  <div style={{ color: 'var(--teal)', fontFamily: 'var(--font-h)', fontSize: 28, fontWeight: 900, marginBottom: 18 }}>{item.step}</div>
                  <h3 style={{ fontFamily: 'var(--font-h)', fontSize: 19, color: 'var(--text)', margin: '0 0 10px', fontWeight: 800 }}>{item.title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: 14, lineHeight: 1.7 }}>{item.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="case-shell" style={{ padding: '86px 5%' }}>
          <div style={{ textAlign: 'center', marginBottom: 42 }}>
            <div className="case-label" style={{ justifyContent: 'center' }}>Client Notes</div>
            <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(30px, 4.5vw, 48px)', color: 'var(--text)', margin: 0, fontWeight: 900 }}>
              What clients value most.
            </h2>
          </div>
          <div className="testimonial-grid" style={{ gridTemplateColumns: 'repeat(3, minmax(0, 1fr))' }}>
            {TESTIMONIALS.map((testimonial) => (
              <div key={testimonial.quote} className="testimonial-card">
                <div style={{ color: 'var(--teal)', fontFamily: 'Georgia, serif', fontSize: 42, lineHeight: 1, marginBottom: 12 }}>"</div>
                <p style={{ color: 'var(--text)', fontSize: 15, lineHeight: 1.8, marginBottom: 24 }}>
                  {testimonial.quote}
                </p>
                <div style={{ borderTop: '1px solid var(--border)', paddingTop: 18 }}>
                  <div style={{ color: 'var(--text)', fontWeight: 800 }}>{testimonial.author}</div>
                  <div style={{ color: 'var(--muted)', fontSize: 13, marginTop: 4 }}>{testimonial.role}, {testimonial.company}</div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="case-shell" style={{ padding: '0 5% 110px' }}>
          <div style={{
            border: '1px solid var(--border)',
            borderRadius: 8,
            background: '#0f172a',
            color: '#fff',
            padding: 'clamp(42px, 7vw, 76px)',
            display: 'grid',
            gridTemplateColumns: '1fr auto',
            gap: 28,
            alignItems: 'center',
          }} className="testimonial-grid">
            <div>
              <div style={{ color: '#93c5fd', fontFamily: 'var(--font-m)', fontSize: 12, fontWeight: 800, textTransform: 'uppercase', marginBottom: 16 }}>Your Case Study Next</div>
              <h2 style={{ fontFamily: 'var(--font-h)', fontSize: 'clamp(32px, 5vw, 58px)', lineHeight: 1.02, margin: '0 0 18px', fontWeight: 900 }}>
                Ready to turn a business problem into a working system?
              </h2>
              <p style={{ color: '#cbd5e1', fontSize: 16, lineHeight: 1.7, maxWidth: 620 }}>
                Share your goal and constraints. We will help you define the scope, timeline, and practical delivery path.
              </p>
            </div>
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <Link to="/contact" className="btn-primary" style={{ padding: '15px 28px', background: '#fff', color: '#0f172a' }}>
                Start a Project
              </Link>
              <Link to="/portfolio" className="btn-outline" style={{ padding: '15px 26px', color: '#fff', borderColor: 'rgba(255,255,255,0.36)' }}>
                View Portfolio
              </Link>
            </div>
          </div>
        </section>
      </div>
    </>
  );
}

